/**
 * Wrapper around the shared LayoutFlow that provides Redux functionality
 * for the financial-advisory frontend.
 */
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { PaneType } from "@financial-advisory/data-access/api-types";
import {
  EvaluatedResponse,
  setActivePaneId,
  setStaticAssignments,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import {
  LayoutFlow as SharedLayoutFlow,
  LayoutFlowProps as SharedLayoutFlowProps,
} from "@unlikelyai-magic/shared/components";
import { TruthValue } from "@unlikelyai-magic/shared/data-access/api";
import { ConditionNode } from "./nodes/ConditionNodeWithRedux";
import { GroupNode } from "./nodes/GroupNodeWithRedux";

export interface LayoutFlowProps
  extends Omit<
    SharedLayoutFlowProps,
    "onNodeClick" | "onPaneClick" | "onHydrateTriggers" | "nodeTypes"
  > {
  /**
   * Optional callback when a node is clicked.
   * If not provided, will dispatch setActivePaneId Redux action.
   */
  onNodeClick?: SharedLayoutFlowProps["onNodeClick"];
  /**
   * Optional callback when the pane is clicked.
   * If not provided, will dispatch setActivePaneId(null) Redux action.
   */
  onPaneClick?: SharedLayoutFlowProps["onPaneClick"];
}

// Re-export LeafNodeValue for backward compatibility
export { type LeafNodeValue } from "@unlikelyai-magic/shared/components";

/**
 * LayoutFlow with Redux integration for financial-advisory.
 * Uses the shared component but adds Redux dispatching for node/pane clicks
 * and hydrates triggers via the onHydrateTriggers callback.
 * Uses custom ConditionNode that reads values from Redux.
 */
export const LayoutFlow = ({
  panes,
  onNodeClick: onNodeClickProp,
  onPaneClick: onPaneClickProp,
  ...props
}: LayoutFlowProps) => {
  const dispatch = useDispatch();

  // Use custom node types with Redux integration
  const nodeTypes = useMemo(
    () => ({
      condition: ConditionNode,
      group: GroupNode,
    }),
    []
  );

  const handleNodeClick = useCallback(
    (nodeId: string, nodeType: string, paneType?: PaneType) => {
      if (onNodeClickProp) {
        onNodeClickProp(nodeId, nodeType, paneType);
      } else {
        // Default Redux behavior
        if (nodeType === "group") {
          if (paneType === PaneType.DEFINITION) {
            dispatch(setActivePaneId(nodeId));
          } else {
            dispatch(setActivePaneId(null));
          }
        }
      }
    },
    [dispatch, onNodeClickProp]
  );

  const handlePaneClick = useCallback(() => {
    if (onPaneClickProp) {
      onPaneClickProp();
    } else {
      // Default Redux behavior
      dispatch(setActivePaneId(null));
    }
  }, [dispatch, onPaneClickProp]);

  const handleHydrateTriggers = useCallback(
    (data: {
      leaves: Record<string, unknown>;
      explanations: Record<string, string>;
    }) => {
      // Pass leaves as TruthValue records to setStaticAssignments
      const evaluatedResponse: EvaluatedResponse = {
        leaves: data.leaves as Record<string, TruthValue>,
        explanations: data.explanations,
      };
      dispatch(setStaticAssignments(evaluatedResponse));
    },
    [dispatch]
  );

  return (
    <SharedLayoutFlow
      {...props}
      panes={panes}
      onNodeClick={handleNodeClick}
      onPaneClick={handlePaneClick}
      onHydrateTriggers={handleHydrateTriggers}
      nodeTypes={nodeTypes}
    />
  );
};
